import React from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet'
import PropTypes from 'prop-types'
import 'leaflet/dist/leaflet.css'

// ─── helpers ──────────────────────────────────────────────────────────────────

const DEPARTAMENTOS = {
    AR: 'Artigas',    CA: 'Canelones', CL: 'Cerro Largo',
    CO: 'Colonia',    DU: 'Durazno',   FS: 'Flores',
    FD: 'Florida',    LA: 'Lavalleja', MA: 'Maldonado',
    MO: 'Montevideo', PA: 'Paysandú',  RN: 'Río Negro',
    RV: 'Rivera',     RO: 'Rocha',     SA: 'Salto',
    SJ: 'San José',   SO: 'Soriano',   TA: 'Tacuarembó',
    TT: 'Treinta y Tres',
}

const CENTRO_URUGUAY = [-32.8, -56.0]
const ZOOM = 6

// ─── component ────────────────────────────────────────────────────────────────

const EstacionesModal = ({ open, onClose, estaciones }) => {

    if (!open) return null

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>

            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h6" component="div" fontWeight={700}>
                    Estaciones disponibles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {estaciones.length} estaciones con datos en esta consulta
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                    aria-label="cerrar"
                >
                    ✕
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>

                {/* Mapa */}
                <Box sx={{ height: 380 }}>
                    <MapContainer
                        center={CENTRO_URUGUAY}
                        zoom={ZOOM}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={false}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {estaciones.map(est => (
                            <CircleMarker
                                key={est.id}
                                center={[est.latitud, est.longitud]}
                                radius={9}
                                pathOptions={{
                                    color: '#1565c0',
                                    fillColor: '#1976d2',
                                    fillOpacity: 0.85,
                                    weight: 2,
                                }}
                            >
                                <Popup>
                                    <strong>{est.displayNamePublic || est.nombre}</strong>
                                    <br />
                                    {DEPARTAMENTOS[est.estado] || est.estado}
                                    <br />
                                    Lat: {est.latitud?.toFixed(4)} | Lon: {est.longitud?.toFixed(4)}
                                    <br />
                                    Alt: {est.altitud} m
                                    <br />
                                    {est.tipoAutomatica ? 'Estación automática' : 'Estación convencional'}
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>
                </Box>

                {/* Tabla */}
                <TableContainer component={Paper} square sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Estación</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Departamento</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Latitud</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Longitud</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Altitud (m)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Cód. OMM</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {estaciones.map(est => (
                                <TableRow key={est.id} hover>
                                    <TableCell>{est.displayNamePublic || est.nombre}</TableCell>
                                    <TableCell>{DEPARTAMENTOS[est.estado] || est.estado || '—'}</TableCell>
                                    <TableCell align="right">{est.latitud?.toFixed(4)}</TableCell>
                                    <TableCell align="right">{est.longitud?.toFixed(4)}</TableCell>
                                    <TableCell align="right">{est.altitud}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={est.tipoAutomatica ? 'Automática' : 'Convencional'}
                                            size="small"
                                            color={est.tipoAutomatica ? 'primary' : 'default'}
                                            variant={est.tipoAutomatica ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {est.codigoOMM
                                            ? est.codigoOMM.replace(/^_/, '')
                                            : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </DialogContent>
        </Dialog>
    )
}

EstacionesModal.propTypes = {
    open:       PropTypes.bool.isRequired,
    onClose:    PropTypes.func.isRequired,
    estaciones: PropTypes.array.isRequired,
}

export default EstacionesModal
